import { action, computed, makeObservable, observable } from 'mobx';
import uniq from 'lodash/uniq';
import compact from 'lodash/compact';
import every from 'lodash/every';
import omit from 'lodash/omit';

import {
    DiagnosisData,
    MedicalCaseData,
    MedicalCaseUpdateParams,
    MedicalCaseVersionData,
    Nullable,
    OutwardReferralData,
    OutwardReferralExecutionChangingParams,
    OutwardReferralUpdatingParams,
    RecommendationData,
    RecommendationUpdateParams,
    TaskData,
    TaskStatisticData,
} from 'chaika-frontend-types/frontend-cms';
import { MedicalCaseStatus, MedicalCaseTaskState, Locale, SortingOrder } from 'chaika-enums';

import { assignParams } from '@utils/assignParams';
import { updateAttributes } from '@utils/updateAttributes';

import { TaskList } from './TaskList';
import { RecommendationList } from './RecommendationList';
import { OutwardReferralList } from './OutwardReferralList';

import { MedicalCaseMutableData } from './MedicalCaseMuttableData';
import { User } from '@store/models/User';
import { OptionType } from '@components/Select';
import { getTranslateByLocale, translate } from '@chaika/i18n/helper';
import { DateFormat, DateHelper } from '@utils/DateHelper';
import { Optional } from 'chaika-common-types';
import { LatestVersionType } from '@patient/ui/shared/Template/SystemBlock/EntryMedicalCases/MedicalCase/MedicalCaseContainer';
import { isDefined } from '@utils/isDefined';
import { store } from '@store';

export interface DiagnosisUpdateParams extends Pick<DiagnosisData, 'icdId' | 'clinicalDiagnosis'> {}
export interface DiagnosisUpdateIcdParams extends Pick<DiagnosisData, 'icdId' | 'clinicalDiagnosis'> {
    oldIcd: string;
}

export const medicalCaseStatusOptions: OptionType<MedicalCaseStatus>[] = [
    {
        value: MedicalCaseStatus.Open,
        label: translate('activeMasculine'),
    },
    {
        value: MedicalCaseStatus.Closed,
        label: translate('closedMasculine'),
    },
];

export const medicalCaseTaskOptions = [
    {
        value: MedicalCaseTaskState.Active,
        label: translate('activeMany'),
    },
    {
        value: MedicalCaseTaskState.Overdue,
        label: translate('overdueMany'),
    },
    {
        value: MedicalCaseTaskState.WithoutActive,
        label: translate('notCreatedMany'),
    },
];

export const medicalCaseFutureReservationOptions: OptionType<string>[] = [
    {
        value: 'true',
        label: translate('have'),
    },
    {
        value: 'false',
        label: translate('no'),
    },
];

export class MedicalCase {
    @observable public readonly id!: string;
    @observable public readonly ehrId!: string;
    @observable public readonly serialNumber!: number;
    @observable public readonly hasFollowup!: boolean;
    @observable public readonly authorId!: string;
    @observable public readonly parentEntryId?: string;
    @observable public readonly createdAt!: string;
    @observable public readonly versionDate!: string;
    @observable public readonly versionAuthorId!: string;
    @observable public readonly versionEntryId?: string;
    @observable public closedAt?: string;
    @observable public diagnoses!: DiagnosisData[];
    @observable public closeData!: MedicalCaseData['closeData'];
    @observable public taskList = new TaskList();
    @observable public recommendationList = new RecommendationList();
    @observable public outwardReferralList = new OutwardReferralList();
    @observable public versions = new Map<string, MedicalCaseVersionData>();
    @observable public isCardExpanded?: boolean;
    @observable public isTreatmentPlanValid?: boolean;
    @observable public status!: MedicalCaseStatus;
    @observable public taskStatistic?: TaskStatisticData;
    @observable public specialtyIds?: string[];
    @observable public hasFutureReservations: boolean = false;

    @observable private readonly addedEntryIds!: string[];
    @observable private outwardReferralIdsSet!: Set<string>;
    @observable private drugIds!: string[];
    @observable private referralIds_!: string[];
    private data: MedicalCaseMutableData;

    constructor({ outwardReferralIds, referralIds, taskStatistic, closeData, ...params }: MedicalCaseData) {
        makeObservable(this);
        this.data = new MedicalCaseMutableData(params);
        assignParams<MedicalCaseData>(
            this as unknown as MedicalCaseData,
            {
                ...omit(params, this.data.mutableKeys),
                versionDate: params.versionDate ?? params.createdAt,
            } as unknown as MedicalCaseData,
        );
        this.taskStatistic = !!taskStatistic ? this.fixTaskStatisticTypes(taskStatistic) : undefined;
        this.referralIds_ = referralIds;
        this.specialtyIds = params.specialtyIds;
        this.outwardReferralIdsSet = new Set(outwardReferralIds);
        this.closeData = closeData;

        if (isDefined(closeData)) {
            const { entry } = store;

            if (closeData.entryId === entry.id) {
                this.status = MedicalCaseStatus.Closed;
            }
        }
    }

    @computed
    public get dto(): Readonly<MedicalCaseData> {
        return {
            ...this.data.serialize(),
            id: this.id,
            ehrId: this.ehrId,
            serialNumber: this.serialNumber,
            authorId: this.authorId,
            parentEntryId: this.parentEntryId,
            status: this.status,
            createdAt: this.createdAt,
            closedAt: this.closedAt,
            versionAuthorId: this.versionAuthorId,
            diagnoses: this.diagnoses,
            addedEntryIds: this.addedEntryIds,
            versionEntryId: this.versionEntryId,
            versionDate: this.versionDate,
            drugIds: this.drugIds,
            referralIds: this.referralIds_,
            specialtyIds: this.specialtyIds,
            outwardReferralIds: this.outwardReferralIds,
            closeData: this.closeData,
            hasFollowup: this.hasFollowup,
        };
    }

    @computed
    public get name(): string {
        return this.data.serialize().name;
    }

    @computed
    public get description(): Optional<string> {
        return this.data.serialize().description;
    }

    @computed
    public get closeComment(): Nullable<string> {
        return this.data.serialize().closeComment;
    }

    @computed
    public get relatedEntriesIds(): string[] {
        return uniq(compact([this.parentEntryId, ...this.addedEntryIds]));
    }

    @computed
    public get diagnosisIcdIds(): string[] {
        return this.diagnoses.map(({ icdId }) => icdId);
    }

    @computed
    public get taskIds(): string[] {
        return this.taskList.ids;
    }

    @computed
    public get referralIds(): string[] {
        return this.referralIds_;
    }

    @computed
    public get hasDrugs(): boolean {
        return this.drugIds.length > 0;
    }

    @computed
    public get hasTasks(): boolean {
        return this.taskIds.length > 0;
    }

    @computed
    public get hasDiagnoses(): boolean {
        return this.diagnoses.length > 0;
    }

    @computed
    public get hasReferrals(): boolean {
        return this.referralIds_.length > 0;
    }

    @computed
    public get hasOutwardReferrals(): boolean {
        return this.outwardReferralIds.length > 0;
    }

    @computed
    public get recommendationIds(): string[] {
        return this.recommendationList.ids;
    }

    @computed
    public get outwardReferralIds(): string[] {
        return Array.from(this.outwardReferralIdsSet.values());
    }

    @computed
    public get outwardReferralLoadedIds(): string[] {
        return this.outwardReferralList.ids;
    }

    @computed
    public get isDeletable(): boolean {
        return !this.isClosed && !this.hasDiagnoses && this.isTreatmentPlanEmpty;
    }

    @computed
    public get isTreatmentPlanEmpty(): boolean {
        return every(
            [
                this.referralIds_,
                this.drugIds,
                this.addedEntryIds,
                this.taskIds,
                this.recommendationList.ids,
                this.outwardReferralList.ids,
            ],
            it => it.length === 0,
        );
    }

    @computed
    public get isOpen(): boolean {
        return this.status === MedicalCaseStatus.Open;
    }

    @computed
    public get isClosed(): boolean {
        return this.status === MedicalCaseStatus.Closed;
    }

    @computed
    public get canReopenCase(): boolean {
        return this.isClosed;
    }

    public get statusSting(): string {
        let text: string;

        switch (this.status) {
            case MedicalCaseStatus.Open: {
                text = `${translate('opened')} ${DateHelper.format(this.createdAt, DateFormat.DateWithMonthString)}`;
                break;
            }
            case MedicalCaseStatus.Closed: {
                text = `${translate('completedMasculine')} ${DateHelper.format(
                    this.closedAt,
                    DateFormat.DateWithMonthString,
                )}`;
                break;
            }
            case MedicalCaseStatus.OnHold: {
                text = `${translate('onHold')} ${DateHelper.format(this.createdAt, DateFormat.DateWithMonthString)}`;
                break;
            }
        }
        return text;
    }

    public get managerId(): string {
        return this.dto.managerId;
    }

    public getStatusStingByLocale(locale: Locale): string {
        let text: string;

        switch (this.status) {
            case MedicalCaseStatus.Open: {
                text = `${getTranslateByLocale(locale, 'opened')} ${DateHelper.format(
                    this.createdAt,
                    DateFormat.DateWithMonthString,
                )}`;
                break;
            }
            case MedicalCaseStatus.Closed: {
                text = `${getTranslateByLocale(locale, 'completedMasculine')} ${DateHelper.format(
                    this.closedAt,
                    DateFormat.DateWithMonthString,
                )}`;
                break;
            }
            case MedicalCaseStatus.OnHold: {
                text = `${getTranslateByLocale(locale, 'onHold')} ${DateHelper.format(
                    this.createdAt,
                    DateFormat.DateWithMonthString,
                )}`;
                break;
            }
        }
        return text;
    }

    public getLatestVersion(viewMode?: boolean): LatestVersionType {
        const { description, versionDate } = this;

        return {
            description,
            label: viewMode
                ? translate('description')
                : `${translate('descriptionFrom')} ${DateHelper.format(
                      String(versionDate),
                      DateFormat.DateWithMonthString,
                  )}`,
        };
    }

    public getSortedDiagnoses(order: SortingOrder): DiagnosisData[] {
        return this.diagnoses.slice().sort((a, b) => {
            const aTime = new Date(a.createdAt).getTime();
            const bTime = new Date(b.createdAt).getTime();

            return order === SortingOrder.Asc ? aTime - bTime : bTime - aTime;
        });
    }

    public isOnStatus(status: MedicalCaseStatus): boolean {
        return this.status === status;
    }

    public canBeClosedByUser(user: User): boolean {
        return this.isManager(user.id) || user.isAssistant;
    }

    public isManager(userId: string): boolean {
        return this.dto.managerId === userId;
    }

    public hasReferral(referralId: string): boolean {
        return this.referralIds_.includes(referralId);
    }

    public relatedToEntry(entryId: string): boolean {
        return this.relatedEntriesIds.includes(entryId);
    }

    public isAuthor(userId: string): boolean {
        return this.authorId === userId;
    }

    public canBeRemovedByUser(userId: string): boolean {
        return userId === this.dto.managerId || userId === this.authorId;
    }

    public getDiagnoses(id: string): Optional<DiagnosisData> {
        return this.diagnoses.find(({ icdId }) => icdId === id);
    }

    public getAllDiagnoses(): DiagnosisData[] {
        return this.diagnoses;
    }

    public getAllOutwardReferral(): OutwardReferralData[] {
        return this.outwardReferralList.values;
    }

    public getTask(id: string): TaskData {
        return this.taskList.getExisting(id);
    }

    public getRecommendation(id: string): RecommendationData {
        return this.recommendationList.getExisting(id);
    }

    public getOutwardReferral(id: string): OutwardReferralData {
        return this.outwardReferralList.getExisting(id);
    }

    @action
    public setIsTreatmentPlanValid(value?: boolean) {
        this.isTreatmentPlanValid = value;
    }

    @action
    public addDrugIds(ids: string[]): void {
        this.drugIds = uniq([...this.drugIds, ...ids]);
    }

    @action
    public removeDrugIds(ids: string[]): void {
        const removingIds = new Set(ids);
        this.drugIds = this.drugIds.filter(drugId => !removingIds.has(drugId));
    }

    @action
    public setVersions(versions: MedicalCaseVersionData[]): void {
        this.versions.clear();

        versions.forEach(medicalCase => {
            this.versions.set(medicalCase.versionDate, medicalCase);
        });
    }

    @action
    public addVersion(version: MedicalCaseVersionData): void {
        this.versions.set(version.versionDate, version);
    }

    @action
    public addReferral(referralId: string): void {
        this.referralIds_ = uniq([...this.referralIds_, referralId]);
    }

    @action
    public addReferrals(referralIds: string[]): void {
        this.referralIds_ = uniq([...this.referralIds_, ...referralIds]);
    }

    @action
    public removeReferral(referralId: string): void {
        this.referralIds_ = this.referralIds_.filter(id => id !== referralId);
    }

    @action
    public removeReferrals(referralIds: string[]): void {
        const removingIds = new Set(referralIds);
        this.referralIds_ = this.referralIds_.filter(id => !removingIds.has(id));
    }

    @action
    public removeOutwardReferrals(outwardReferralIds: string[]): void {
        outwardReferralIds.forEach(id => this.outwardReferralIdsSet.delete(id));
        outwardReferralIds.forEach(id => this.outwardReferralList.remove(id));
    }

    @action
    public addDiagnosis(diagnosis: DiagnosisData): void {
        this.diagnoses = [...this.diagnoses, diagnosis];
    }

    @action
    public updateDiagnosis({ icdId, clinicalDiagnosis }: DiagnosisUpdateParams): void {
        this.diagnoses = this.diagnoses.map(diagnosis =>
            diagnosis.icdId === icdId ? { ...diagnosis, icdId, clinicalDiagnosis } : diagnosis,
        );
    }

    @action
    public updateDiagnosisIcd({ icdId, clinicalDiagnosis, oldIcd }: DiagnosisUpdateIcdParams): void {
        this.diagnoses = this.diagnoses.map(diagnosis =>
            diagnosis.icdId === oldIcd ? { ...diagnosis, icdId, clinicalDiagnosis } : diagnosis,
        );
    }

    @action
    public removeDiagnosis(icdId: string): void {
        this.diagnoses = this.diagnoses.filter(diagnosis => diagnosis.icdId !== icdId);
    }

    @action
    public update({
        managerId,
        name,
        description,
        closeComment,
        shareLink,
        isReferralsRequired,
        ...params
    }: Omit<MedicalCaseUpdateParams, 'entryId'> & { shareLink?: string }): void {
        this.data.update({ managerId, name, description, closeComment, shareLink, isReferralsRequired });
        updateAttributes(this, params, true);
    }

    @action
    public updateCloseData(closeData: MedicalCaseData['closeData']): void {
        this.closeData = closeData;
    }

    @action
    public setRecommendations(recommendations: RecommendationData[]): void {
        this.recommendationList.set(recommendations);
    }

    @action
    public addRecommendation(recommendation: RecommendationData): void {
        this.recommendationList.add([recommendation]);
    }

    @action
    public updateRecommendation(recommendationId: string, recommendation: RecommendationUpdateParams): void {
        this.recommendationList.update(recommendationId, recommendation);
    }

    @action
    public removeRecommendation(recommendationId: string): void {
        this.recommendationList.remove(recommendationId);
    }

    public hasOutwardReferral(outwardReferralId: string): boolean {
        return this.outwardReferralIdsSet.has(outwardReferralId);
    }

    @action
    public setOutwardReferrals(outwardReferrals: OutwardReferralData[]): void {
        outwardReferrals.forEach(({ id }) => {
            this.outwardReferralIdsSet.add(id);
        });
        this.outwardReferralList.set(outwardReferrals);
    }

    @action
    public addOutwardReferral(outwardReferral: OutwardReferralData): void {
        this.outwardReferralIdsSet.add(outwardReferral.id);
        this.outwardReferralList.add([outwardReferral]);
    }

    @action
    public updateOutwardReferral(
        outwardReferralId: string,
        outwardReferral: OutwardReferralUpdatingParams & Partial<OutwardReferralExecutionChangingParams>,
    ): void {
        this.outwardReferralList.update(outwardReferralId, outwardReferral as Partial<OutwardReferralData>);
    }

    @action
    public updateOutwardReferralExecuted(
        outwardReferralId: string,
        outwardReferral: OutwardReferralExecutionChangingParams,
    ): void {
        this.outwardReferralList.update(outwardReferralId, outwardReferral);
    }

    @action
    public removeOutwardReferral(outwardReferralId: string): void {
        this.outwardReferralIdsSet.delete(outwardReferralId);
        this.outwardReferralList.remove(outwardReferralId);
    }

    @action
    public setHasFutureReservations(hasFutureReservations: boolean) {
        this.hasFutureReservations = hasFutureReservations;
    }

    private fixTaskStatisticTypes(taskStatisticData: TaskStatisticData): TaskStatisticData {
        return {
            ...taskStatisticData,
            activeTaskCount: Number(taskStatisticData.activeTaskCount),
            overdueTaskCount: Number(taskStatisticData.overdueTaskCount),
            totalTaskCount: Number(taskStatisticData.totalTaskCount),
        };
    }
}
